import React from 'react';
import { organisationsAPI } from '../../../organisations/api/organisationsAPI';
import { useStudentSection } from '../../hooks/useStudentSection';
import EmptyState from '../common/EmptyState';
import { EM_DASH, longDate, orDash } from '../../lib/format';

// The server logs every read as a view, so this is read only here, when the
// tab is opened, and shown only here: not in the students table, the CSV
// export or the header chips.
const fetchSensitiveDetails = (studentId) => organisationsAPI.getStudentSensitiveDetails(studentId);

const yesNo = (value) => (value == null ? EM_DASH : value ? 'Yes' : 'No');

const SEN_STATUS_LABELS = {
  sen_support: 'SEN Support',
  ehcp: 'EHC Plan',
  none: 'No SEN status recorded',
};

const ADMISSION_ROUTE_LABELS = {
  internal: "Internal (from the school's Year 11)",
  external: 'External (joined in Year 12)',
};

const BACKGROUND_FIELDS = [
  'free_school_meals', 'pupil_premium', 'bursary_16_19', 'eal',
  'sen_status', 'sen_needs', 'sen_description', 'admission_route',
];

const NO_DATA = 'No details for this student yet.';

/** A heading and its terms. Values are the school's text, rendered as text. */
const Group = ({ title, rows }) => (
  <section className="td-details__group">
    <h3 className="td-card__title">{title}</h3>
    <dl className="td-details__list">
      {rows.map(([term, value]) => (
        <div key={term} className="td-details__row">
          <dt>{term}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  </section>
);

/**
 * The school's background details for a student, from
 * GET /api/organisations/students/<id>/sensitive-details: support, SEN,
 * how they joined the sixth form, and extenuating circumstances. The server
 * serves it only to the school's org admin; this panel does not check.
 */
const StudentOtherDetailsPanel = ({ studentId }) => {
  const { status, data, message } = useStudentSection(
    fetchSensitiveDetails, studentId, 'Could not load these details. Please try again.',
  );

  if (status === 'loading') return <p className="td-loading">Loading details…</p>;
  if (status === 'forbidden') return <EmptyState tone="warning" title="You don't have access to this." />;
  if (status === 'off') return <EmptyState title={NO_DATA} />;
  if (status === 'error') return <EmptyState tone="warning" title="Could not load these details" body={message} />;

  const extenuating = data.extenuating_circumstances;
  if (extenuating == null && BACKGROUND_FIELDS.every((f) => data[f] == null)) return <EmptyState title={NO_DATA} />;

  const extenuatingRows = [['Recorded', extenuating == null ? 'None recorded' : yesNo(extenuating.flagged)]];
  if (extenuating?.details) extenuatingRows.push(['Details', extenuating.details]);
  if (data.extenuating_circumstances_as_of) extenuatingRows.push(['As of', longDate(data.extenuating_circumstances_as_of)]);

  return (
    <div className="td-schooldata td-details">
      <p className="td-schooldata__note">Visible to school admins only.</p>
      {data.data_as_of ? <p className="td-schooldata__asof">{`School data as of ${longDate(data.data_as_of)}`}</p> : null}

      <Group
        title="Support"
        rows={[
          ['Free School Meals', yesNo(data.free_school_meals)],
          ['Pupil Premium', yesNo(data.pupil_premium)],
          ['16–19 Bursary', yesNo(data.bursary_16_19)],
          ['EAL', yesNo(data.eal)],
        ]}
      />
      <Group
        title="SEN"
        rows={[
          ['Status', SEN_STATUS_LABELS[data.sen_status] || EM_DASH],
          ['Needs', orDash(data.sen_needs)],
          ['Description', orDash(data.sen_description)],
        ]}
      />
      <Group title="Joined" rows={[['Route', ADMISSION_ROUTE_LABELS[data.admission_route] || EM_DASH]]} />
      <Group title="Extenuating circumstances" rows={extenuatingRows} />
    </div>
  );
};

export default StudentOtherDetailsPanel;
